import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getCurrentUserRole } from '../../services/shipturtleApi';
import DataFallback from './DataFallback';
import { classifyError, ERROR_KIND, TERMINAL_COPY } from '../../utils/errorTaxonomy';

/**
 * Admin-only route gate that tells the truth about why it is blocking.
 *
 * Several Terminal routes did this themselves, and all made the same conflation:
 * a failed role lookup was treated as "not an admin", so an actual admin was
 * silently redirected to Orders during a deploy, with no message and nothing to
 * retry. The page simply refused to exist. Worse, `if (loading) return null`
 * meant a role request that never came back left a permanently blank route.
 *
 * Failing closed is still right — nobody is admitted on an unknown role. But
 * "we could not check" and "you are not allowed" deserve different outcomes:
 * the first is ours to fix and is worth retrying, the second is final and is
 * worth a redirect.
 */
function AdminRouteGate({ children, redirectTo = '/suspire-terminal/orders' }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCurrentUserRole();
        if (!cancelled) {
          setRole(data?.role || null);
          setError(null);
        }
      } catch (err) {
        // Deliberately does not set a role. An unknown role is never admitted,
        // so the gate stays closed either way — this only decides whether we
        // explain ourselves or vanish.
        if (!cancelled) setError(err && err.classified ? err.classified : classifyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attempt]);

  if (loading) {
    // Previously `return null`, which left a blank route for as long as the
    // request hung — and forever if it never returned.
    return (
      <div style={{ padding: '1.5rem' }}>
        <p style={{ color: '#64748b' }}>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <DataFallback
          variant="pane"
          error={error}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }

  if (role !== 'admin') {
    // A definite answer: this account does not have the section. Redirecting is
    // right here, because there is nothing to wait for.
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

/** The copy shown when a role lookup succeeded and the answer was no. */
export const NOT_ADMIN_COPY = {
  kind: ERROR_KIND.FORBIDDEN,
  title: TERMINAL_COPY.forbidden.title,
  message: TERMINAL_COPY.forbidden.message,
  retryable: false,
};

export default AdminRouteGate;
